import angular from 'angular';
import moment from 'moment-timezone';
import '../../services/appointment';
import '../../services/appointment-reservation';
import '../../services/area';
import '../../services/errors';
import '../../services/holiday';
import '../../services/validation';
import '../../services/notifications';
import '../../services/date-handler';
import '../../services/identification';
import {
  get,
  includes,
  toUpper,
  toLower,
  filter,
  forEach,
  isEmpty,
  set,
  unset,
  isNull,
  kebabCase,
  camelCase,
  head,
} from 'lodash';
import { countriesWithCodes } from '../../utils/countries-and-codes';
import { AreaCalendarType } from '../../utils/areaCalendarType';
import { CreatedFromEnum } from '../../utils/createdFromEnum';
import { CalledFromEnum } from '../../utils/calledFromEnum';
import './maximum-appointment-per-day-and-person';

import Gender from '../../enums/gender';
import PersonType from '../../enums/personType';
import IdentificationType from '../../enums/identificationType';
import { buildPersonDataFromClientResponse } from '../../utils';

const module = angular.module('whyline.modals.edit-appointment', [
  'whyline.services.appointment',
  'whyline.services.appointment-reservation',
  'whyline.services.area',
  'whyline.services.errors',
  'whyline.services.validation',
  'whyline.services.notifications',
  'whyline.services.date-handler',
  'whyline.services.holiday',
  'whyline.modals.maximum-appointment-per-day-and-person',
  'whyline.directives.extraDateAppointment',
  'whyline.services.identification',
  'whyline.factories.socket',
]);

const EditAppointmentController = (
  $scope,
  $rootScope,
  $uibModalInstance,
  $timeout,
  appointment,
  area,
  $translate,
  AppointmentService,
  AppointmentReservationService,
  AreaService,
  DateHandlerService,
  ErrorService,
  HolidayService,
  NotificationService,
  PlaceService,
  ValidationService,
  dialog,
  $uibModal,
  IdentificationService,
  SocketService
) => {
  'ngInject';

  let toastrInstance = null;

  const appointmentId = appointment._id ? appointment._id : false;

  $scope.idTypesList = PlaceService.getIdentificationsForCurrentPlace() || [];
  $scope.appointment = AppointmentService.Copy(appointment);

  $scope.idLimits = {
    physicalPerson: {
      min: 6,
      max: 8,
    },
    legalPerson: {
      min: 9,
      max: 11,
    },
  };

  $scope.submitIdClicked = false;

  $scope.currentGender = '';
  $scope.isLegalPerson = false;
  $scope.currentPerson = PersonType.physicalPerson;
  $scope.minLength = $scope.idLimits[$scope.currentPerson].min;
  $scope.maxLength = $scope.idLimits[$scope.currentPerson].max;
  $scope.responseAll = null;

  $scope.setEnterprise = () => {
    $scope.enterprise = !$scope.enterprise;
    $scope.isLegalPerson = $scope.enterprise;
    $scope.currentPerson = $scope.enterprise ? PersonType.legalPerson : PersonType.physicalPerson;
    $scope.minLength = $scope.idLimits[$scope.currentPerson].min;
    $scope.maxLength = $scope.idLimits[$scope.currentPerson].max;
    $scope.currentGender = '';
    $scope.appointment.person.id = '';
    $scope.resetPersonData();
    $scope.appointment.person.idType = toLower($scope.isLegalPerson ? IdentificationType.CUIT : IdentificationType.DNI);
    $scope.submitIdClicked = false;
  }

  $scope.setPerson = (newPerson) => {
    $scope.isLegalPerson = PersonType.legalPerson === PersonType[newPerson];
    $scope.currentPerson = PersonType[newPerson];
  }

  $scope.setCurrentGender = (newGender) => {
    $scope.currentGender = Gender[camelCase(newGender)];
    $scope.resetPersonData();
    $scope.submitIdClicked = false;
  }

  $scope.keyPressValidate = (event) => {
    event.target.value = event.target.value.trim();
    if (event.target.value.toString().startsWith('0')) {
      event.target.value = trimStart(event.target.value, '0');
    }

    let value = event.target.value;

    if (value.length > $scope.maxLength) {
      event.target.value = value.slice(0, $scope.maxLength);
    }

    $scope.appointment.person['id'] = event.target.value;
    $scope.resetPersonData();
    $scope.submitIdClicked = false;
  }

  const validateName = (event, regexp) => {
    const cleanName = event.target.value.replace(new RegExp(regexp), '').trimStart();
    event.target.value = cleanName;

    if (event.currentTarget.name === 'firstName') {
      $scope.appointment.person.firstName = cleanName;
    } else {
      $scope.appointment.person.lastName = cleanName;
    }
  };

  $scope.validateNames = (event) => {
    validateName(event, /[^ña-zÑA-ZüÜ~¨\s]+/g);
  };

  $scope.enableSearch = () => {
    const idLength = !isEmpty(get($scope, 'appointment.person.id', ''))
      ? $scope.appointment.person.id.length
      : 0;

    const isValidGenderOrNonRequired = $scope.isLegalPerson 
                                        || !$scope.showGender 
                                        || ($scope.showGender && !isEmpty($scope.currentGender));

    const isValidId = idLength >= $scope.minLength && idLength <= $scope.maxLength;
    const enableSearch = isValidGenderOrNonRequired && isValidId;
    return enableSearch && !$scope.disabledIdentifiersDniOrCuit;
  }

  $scope.submitId = () => {
    if (!$scope.enableSearch()) return;

    // Force temporary id type for initial validation, before send request to external service.
    // After external service responds, we take final identification type and overwrite this one.
    $scope.appointment.person.idType = toLower($scope.currentPerson === PersonType.legalPerson
      ? IdentificationType.CUIT
      : IdentificationType.DNI
    );
    if (!ValidationService.validatePerson($scope.appointment.person, 'personId', $scope, { timeOut: 10000 }, 'appointments')) return;

    $scope.responseAll = null;
    $scope.submitIdClicked = false;
    IdentificationService
      .Identify(
        $scope.appointment.person.id,
        $scope.currentGender,
        $scope.place.organizationId,
        CalledFromEnum.VirtualineAgenda
      )
      .then(response => {
        $scope.submitIdClicked = true;
        $scope.responseAll = response;

        if (get(response, 'moreThanOneUser', true)) {
          $scope.showGender = true;
          $scope.submitIdClicked = false;
          return;
        }

        if (get(response, 'client', false)) {
          const person = buildPersonDataFromClientResponse(response);
          $scope.appointment.person = person;
        } else {
          NotificationService.Warning($translate.instant('client_identify_error'));
          $scope.resetPersonData();
        }
      })
      .catch(error => {
        $scope.submitIdClicked = true;
        $scope.responseAll = error;
        NotificationService.Warning($translate.instant('client_identify_error'));
        $scope.resetPersonData();
      });
  }

  $scope.resetPersonData = () => {
    $scope.appointment.person = Object.assign({}, $scope.appointment.person, {
      firstName: '',
      lastName: '',
      phone: {
        countryCode: 'AR',
        full: '',
      },
      email: '',
    });
  };

  $scope.modalTitle = appointmentId ? 'edit_appointment' : 'new_appointment';
  $scope.isEdit = !!appointmentId;
  $scope.area = area ? AreaService.Copy(area) : false;
  $scope.place = PlaceService.GetCurrent();
  $scope.placeUTCOffset = moment
    .tz($scope.place.timezone)
    .format('Z')
    .toString();
  $scope.format = $translate.use() === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
  $scope.countries = countriesWithCodes;
  $scope.fetchSlotsExecuted = false;
  $scope.hoursOutOfRange = false;
  $scope.useSharedAgenda = true;
  $scope.showGender = false;
  
  const defaultPlaceId = $scope.idTypesList.find(id => id.default);
  const isAgendaEnabled = $scope.place.configuration.canGiveAppointments;
  $scope.isExtraAppointment = false;

  $scope.init = () => {
    $scope.personKeys = Object.keys(PersonType);
    $scope.genderKeys = Object.keys(Gender)
      .filter(gender => gender && gender !== 'none')
      .map(gender => kebabCase(gender));

    let selectedId = null;
    observingSchedule();

    // Set appointment editing configuration
    if (appointmentId) {
      selectedId = $scope.idTypesList.find(
        id => id.key === $scope.appointment.person.idType,
      );
      $scope.appointment.person.phone.countryCode = $scope.appointment.person
        .phone.countryCode
        ? $scope.appointment.person.phone.countryCode
        : toUpper($scope.place.location.countryCode);
      $scope.appointmentDate = {
        calendar: DateHandlerService.getNeutralDate(
          $scope.appointment.appointment.date,
        ),
        selected: $scope.appointment.appointment.date,
        showProcessMessage: false,
      };
    } else {
      set(
        $scope.appointment,
        'person.phone.countryCode',
        toUpper($scope.place.location.countryCode),
      );
      set($scope.appointment, 'person.idType', defaultPlaceId.key);
    }

    // Options Id type
    if (!isEmpty(selectedId)) {
      $scope.selectedIdType = {
        value: selectedId,
      };
    } else {
      $scope.selectedIdType = {
        value: defaultPlaceId,
      };
      set($scope.appointment, 'person.idType', defaultPlaceId.key);
    }

    // Agenda options
    $scope.daysClosed = [];
    $scope.datesClosed = [];
    if (isAgendaEnabled && $scope.area) {
      $scope.fetchSlots();
      closedDays();
      setCalendarOptions();
    }
    if (!$scope.area) {
      $scope.fetchAreas();
    }

    validateIdentificationsEnabled($scope.place);
  };

  $scope.fetchSlots = () => {
    const momentDate = moment($scope.appointmentDate.calendar);

    if (!momentDate.isValid()) {
      return NotificationService.Warning(
        $translate.instant('select_date_area_and_apply_filters'),
      );
    }

    const selectedDate = momentDate.format('YYYY-MM-DD');

    AreaService.GetSlotsByHour({
      queuerIds: [$scope.area._id],
      queuerType: 'area',
      hideEmptySlots: false,
      showWorkingDay: false,
      date: selectedDate,
      addNextDay: true,
      placeId: $scope.place._id,
    }).then(slots => {
      $scope.slotToday = slots.today;
      $scope.enabledExtraAppointment = get($scope.place, 'configuration.extraAppointment.enabled', false);      
      const quantityExtraAppointment = get($scope.place, 'configuration.extraAppointment.quantity', 1);

      let firstDate = true;
      const duration = get($scope.area, ($scope.useSharedAgenda) ? 'firstLine.configuration.appointments.duration' : 'appointmentsConfiguration.duration', 0);
      $scope.temporaryDuration = duration;
      $scope.slots = filter(slots.today, (slot, index, array) => {
        let availability = true;

        if (
          selectedDate ===
          moment(slot.hour)
            .tz($scope.place.timezone)
            .startOf('day')
            .format('YYYY-MM-DD')
        ) {
          const appointmentDateUTCFormat = moment(
            $scope.appointment.appointment.date,
          ).utc();
          const appointmentEndDateUTCFormat = moment(
            $scope.appointment.appointment.endDate,
          ).utc();

          for (
            let i = 0;
            i < $scope.area.appointmentsConfiguration.timeSlotsQuantity;
            i++
          ) {
            let currentSlot = false;

            const thisSlot = array[index + i];
            const nextSlot = array[index + i + 1];
            const lastSlotQuantity =
              $scope.area.appointmentsConfiguration.timeSlotsQuantity !== i + 1;

            if (
              !thisSlot ||
              (thisSlot &&
                nextSlot &&
                lastSlotQuantity &&
                moment(thisSlot.hour)
                  .utc()
                  .add(
                    duration,
                    'minutes',
                  )
                  .format() !==
                moment(nextSlot.hour)
                  .utc()
                  .format())
            ) {
              availability = false;

              break;
            }

            const thisSlotHourUTC = moment(thisSlot.hour).utc();

            if (
              thisSlotHourUTC.isBetween(
                appointmentDateUTCFormat,
                appointmentEndDateUTCFormat,
                null,
                '[)',
              )
            ) {
              currentSlot = true;
              firstDate = false;

              if (thisSlotHourUTC.isSame(appointmentDateUTCFormat)) {
                $scope.appointmentDate.selected = slot.hour;
              }
            }

            const now = moment()
              .tz($scope.place.timezone)
              .format();
            const slotTimezone = moment(thisSlot.hour)
              .tz($scope.place.timezone)
              .format();
            const isInThePast = moment(now).isAfter(slotTimezone);
            const realTotalSlots = $scope.enabledExtraAppointment ? thisSlot.slots + quantityExtraAppointment : thisSlot.slots;
            if (
              !(
                currentSlot ||
                (thisSlot.reserved < realTotalSlots && !isInThePast)
              )
            ) {
              availability = false;
              break;
            }
          }
        } else {
          availability = false;
        }

        return availability;
      });

      $scope.fetchSlotsExecuted = true;

      if (firstDate && $scope.slots.length) {
        $scope.appointmentDate.selected = $scope.slots[0].hour;
        $timeout(() => $scope.isExtraAppointment = $scope.slots[0]['extraAppointment']);
      } else if (!$scope.slots.length) {
        $scope.appointmentDate.selected = null;
      }

      validateProcessConfig();
    });
  };

  $scope.fetchAreas = () => {
    $scope.areas = [];
    AreaService.GetAgenda().then(areas => {
      $timeout(() => {
        Object.keys(areas).forEach(key => {
          if (areas[key].appointmentsConfiguration.enabled && areas[key].open) {
            $scope.areas.push(areas[key]);
          }
        });
      });
    })
      .catch(ErrorService.handler);
  };

  $scope.changeSelectedArea = selectedArea => {
    $scope.daysClosed = [];
    $scope.datesClosed = [];
    $scope.area = selectedArea;
    set(
      $scope.appointment,
      'notes.label',
      $scope.area.appointmentsConfiguration.notes.label || null,
    );
    closedDays();
    setCalendarOptions();

    $scope.appointmentDate = {
      calendar: DateHandlerService.getNeutralDate(),
      selected: null,
    };
    $scope.fetchSlots();
  };

  $scope.updateIdType = () => {
    $scope.appointment.person.idType = get(
      $scope.selectedIdType,
      'value.key',
      undefined,
    );
  };

  $scope.save = () => {
    $scope.appointment.person.identifications = buildIdentifications(
      $scope.appointment.person.id,
      $scope.appointment.person.idType,
    );

    if (
      ValidationService.validatePerson($scope.appointment.person, 'personId', $scope, { timeOut: 10000 }, 'appointments',)
      && ValidationService.validatePerson($scope.appointment.person, 'name', $scope, { timeOut: 10000 }, 'appointments',) 
      && ValidationService.validatePerson($scope.appointment.person, 'phone', $scope, { timeOut: 10000 }, 'appointments',) 
      && ValidationService.validatePerson($scope.appointment.person, 'email', $scope, { timeOut: 10000 }, 'appointments',)
      && validate()
    ) {
      $scope.blockSaveButton = true;
      if (isAgendaEnabled) {
        $scope.appointment.appointment.date = $scope.appointmentDate.selected;
        $scope.appointment.appointment.ISODate =
          $scope.appointmentDate.selected;
      }
      if (appointmentId) {
        $scope.appointment.updatedFrom = CreatedFromEnum.virtualineAgenda;
        AppointmentService.Update($scope.appointment)
          .then(() => {
            $scope.blockSaveButton = false;
            NotificationService.Success(
              $translate.instant('appointment_edit_succ'),
            );
            $uibModalInstance.close('saved');
          })
          .catch((error) => {
            if (error.data) {
              const errorNotification = get(error, 'data.message', '');
              const errorCode = get(error, 'data.httpCode', 0);
              const appointmentsTodayIds = get(error, 'data.data', []);

              const singleDailyAppointment = {
                errorCode: 405,
                message: 'appointments.max-appointment-per-day-and-person',
              };
              if (
                singleDailyAppointment.errorCode === errorCode &&
                singleDailyAppointment.message === errorNotification
              ) {
                const modal = $uibModal.open({
                  templateUrl: '/templates/components/modals/maximum-appointment-per-day-and-person-modal.html',
                  size: 'lg',
                  controller: 'MaximumAppointmentPerDayAndPersonModalController',
                  controllerAs: 'modal',
                  windowClass: 'increase-z-index',
                });

                modal.result.then(result => {
                  if (result === 'confirm') {
                    const gender = get($scope.appointment, 'person.gender', null);

                    if (isNull(gender)) {
                      unset($scope.appointment, 'person.gender');
                    }

                    appointmentsTodayIds.push($scope.appointment._id);

                    AppointmentReservationService.deletePreviousAppointmentsAndRegisterANewAppointment(
                      $scope.place._id,
                      $scope.area._id,
                      $scope.appointment.person,
                      $scope.appointment.appointment.date,
                      $scope.appointment.appointment.date,
                      $scope.appointment.notes,
                      CreatedFromEnum.virtualineAgenda,
                      appointmentsTodayIds,
                    ).then(() => {
                      $scope.blockSaveButton = false;
                      NotificationService.Success($translate.instant('appointment_edit_succ'));
                      $uibModalInstance.close('saved');
                    });
                  }
                });
              } else {
                ErrorService.handler(error);
              }
            } else {
              ErrorService.handler(error);
            }
          });
      } else {
        AppointmentReservationService.createAppointmentReservation(
          $scope.place._id,
          $scope.area._id,
          $scope.appointment.person,
          $scope.appointment.appointment.date,
          $scope.appointment.appointment.date,
          $scope.appointment.notes,
          CreatedFromEnum.virtualineAgenda,
        )
          .then((res) => {
            const isExtraAppointment = get(res, 'data.isExtraAppointment', false);
            const successMessage = isExtraAppointment ? 'extra_appointment_successful' : 'appointment_create_succ';
            $scope.blockSaveButton = false;

            NotificationService.Success(
              $translate.instant(successMessage),
            );

            $uibModalInstance.close('saved');
          })
          .catch((error) => {
            $scope.blockSaveButton = false;
            if (error.data) {
              const errorNotification = get(error, 'data.message', '');
              const errorCode = get(error, 'data.httpCode', 0);
              const appointmentsTodayIds = get(error, 'data.data', []);

              const singleDailyAppointment = {
                errorCode: 405,
                message: 'appointments.max-appointment-per-day-and-person',
              };
              if (
                singleDailyAppointment.errorCode === errorCode &&
                singleDailyAppointment.message === errorNotification
              ) {
                const modal = $uibModal.open({
                  templateUrl: '/templates/components/modals/maximum-appointment-per-day-and-person-modal.html',
                  size: 'lg',
                  controller: 'MaximumAppointmentPerDayAndPersonModalController',
                  controllerAs: 'modal',
                  windowClass: 'increase-z-index',
                });

                modal.result.then(result => {
                  if (result === 'confirm') {
                    AppointmentReservationService.deletePreviousAppointmentsAndRegisterANewAppointment(
                      $scope.place._id,
                      $scope.area._id,
                      $scope.appointment.person,
                      $scope.appointment.appointment.date,
                      $scope.appointment.appointment.date,
                      $scope.appointment.notes,
                      CreatedFromEnum.virtualineAgenda,
                      appointmentsTodayIds,
                    ).then(() => {
                      $scope.blockSaveButton = false;
                      NotificationService.Success($translate.instant('appointment_create_succ'));
                      $uibModalInstance.close('saved');
                    });
                  }
                });
              } else {
                ErrorService.handler(error);
              }
            } else {
              ErrorService.handler(error);
            }
          });
      }
    }
  };

  $scope.cancel = () => {
    $uibModalInstance.close('cancel');
  };

  $scope.preventSpace = event => {
    if (event.keyCode === 32) {
      event.preventDefault();
    }
  }

  const validate = () => {
    if (isAgendaEnabled) {
      if (get($scope.place, 'requirements.phoneNumber', false) && !$scope.appointment.person.phone.full) {
        NotificationService.Warning($translate.instant('phone_required'));
        return false;
      }
      if (!$scope.appointment.person.email) {
        NotificationService.Warning($translate.instant('mandatory_email'));
        return false;
      }
      if ($scope.appointmentDate && !$scope.appointmentDate.selected) {
        NotificationService.Warning(
          $translate.instant('no_appointment_available'),
        );
        return false;
      }
      if (
        $scope.area.appointmentsConfiguration.notes.required &&
        !$scope.appointment.notes.text
      ) {
        NotificationService.Warning($translate.instant('mandatory_notes'));
        return false;
      }
    }

    return true;
  };

  const setMaxDate = () => {
    const todayPlace = moment()
      .tz($scope.place.timezone)
      .startOf('day');
    return moment(todayPlace)
      .add(1, 'year')
      .toDate();
  };

  const setMinDate = () => {
    const todayPlace = moment().tz($scope.place.timezone);
    return todayPlace.startOf('day').toDate();
  };
  const observingSchedule = () => {
    $scope.$watch('appointmentDate.selected', (newValue, oldValue) => {
      if (newValue === oldValue) {
        return;
      }

      if (newValue === null) {
        newValue = moment.tz($scope.place.timezone);
      }
      outOfRangeWarning(newValue);
      if ($scope.slots) {
        const slot = $scope.slots.find(slot => slot.hour === newValue)
        if (slot) {
          $timeout(() => $scope.isExtraAppointment = !!slot.extraAppointment);
        }
      }
    });
  }

  const outOfRangeWarning = (schedule) => {
    $scope.hoursOutOfRange = false;
    const dayToCheck = $scope.appointmentDate.calendar;
    const timezone = $scope.area.timezone;
    const currentTime = moment.tz(timezone);
    const isToday = moment(dayToCheck).tz(timezone).isSame(currentTime, 'day');
    const selectedSchedule = moment(schedule).tz(timezone);
    const enableMinutesToCreateAnAppointment = get($scope.area, 'appointmentsConfiguration.dynamicPeriod.enableMinutesToCreateAnAppointment.enabled', false);
    const since = get($scope.area, 'appointmentsConfiguration.dynamicPeriod.since', false);
    const until = get($scope.area, 'appointmentsConfiguration.dynamicPeriod.until', false);
    const onlyToday = !since && !until;

    if (enableMinutesToCreateAnAppointment && isToday) {
      const addMinutes = get($scope.area, 'appointmentsConfiguration.dynamicPeriod.enableMinutesToCreateAnAppointment.minutes', 0);
      const newTime = moment(currentTime).add(addMinutes, 'minutes');
      $scope.hoursOutOfRange = !moment(selectedSchedule).isSameOrAfter(newTime);
    } else if (onlyToday && !isToday) {
      $scope.hoursOutOfRange = onlyToday;
    }

  }


  const validateProcessConfig = () => {
    const serviceCalendarType =
      $scope.area.appointmentsConfiguration.areaCalendarType;
    const dayToCheck = $scope.appointmentDate.calendar;

    if (serviceCalendarType === AreaCalendarType.ForADynamicPeriod) {
      $scope.appointmentDate.showProcessMessage = !DateHandlerService.dynamicPeriod(
        $scope.area.appointmentsConfiguration.dynamicPeriod,
        $scope.daysClosed,
        dayToCheck,
        $scope.place.timezone,
      );
    } else if (serviceCalendarType === AreaCalendarType.ForAFixedPeriod) {
      $scope.appointmentDate.showProcessMessage = !DateHandlerService.fixedPeriod(
        $scope.area.appointmentsConfiguration.fixedPeriod,
        $scope.daysClosed,
        dayToCheck,
        $scope.place.timezone,
      );
    }
  };

  const buildIdentifications = (id, idType) =>
    Object.assign({}, $scope.appointment.person.identifications, {
      [`${idType}`]: { type: idType, value: id },
    });

  // Date picker
  const setCalendarOptions = () => {
    $scope.dateOptions = {
      formatYear: 'yy',
      minDate: setMinDate(),
      maxDate: setMaxDate(),
      startingDay: 1,
      dateDisabled: date =>
        includes($scope.daysClosed, date.date.getDay()) ||
        includes($scope.datesClosed, moment(date.date).format('DD/MM/YYYY')),
    };
  };

  const closedDays = () => {
    forEach($scope.area.schedule, day => {
      if (!day.open) {
        $scope.daysClosed.push(day.number);
      }
    });
    HolidayService.GetAllForArea($scope.area._id)
      .then(response => {
        $scope.datesClosed = response.data;
      })
      .catch(ErrorService.handler);
  };

  const getValidatorId = (place, idType) => {
    const identifications = place.requirements.identifications;
    return identifications.find(id => id.key === idType);
  }

  const validateIdentificationsEnabled = (place) => {
    if (place.requirements.identifications) {
      place.requirements.identifications = place.requirements.identifications
        .filter(id => id.show)
        .map(id => {
          return id;
        });
    }

    const functionValidatorDniToString = get(getValidatorId(place, 'dni'), 'validator', '');
    const functionValidatorCuitToString = get(getValidatorId(place, 'cuit'), 'validator', '');
    $scope.disabledIdentifiersDniOrCuit = !functionValidatorDniToString || !functionValidatorCuitToString;
    if ($scope.disabledIdentifiersDniOrCuit) {
      toastrInstance = NotificationService.Error($translate.instant('unavailable-validations'), {
        timeOut: 0,
        preventDuplicates: true,
        preventOpenDuplicates: true,
        maxOpened: 5,
        closeButton: false,
        extendedTimeOut: 0,
        tapToDismiss: false,
      });
    } else {
      NotificationService.Clear(toastrInstance);
    }
  }

  $scope.$on('$destroy', () => {
    if(toastrInstance) {
      NotificationService.Clear(toastrInstance);
    }
  });

  if(SocketService.GetConnected()) {
    SocketService.GetInstance().then((socket) => {
      socket.on('place:updated', place => {
        validateIdentificationsEnabled(place);
      });
    });
  } else {
    $rootScope.$on('socketReady', async () => {
      const socket = await SocketService.GetInstance();
      socket.on('place:updated', place => {
        validateIdentificationsEnabled(place);
      });
    });
  }

  $scope.init();
};

module.exports = module.controller(
  'EditAppointmentController',
  EditAppointmentController,
);
